import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi, compartilhamentosApi, notificacoesApi } from '../api/endpoints'
import { useAuthStore } from '../store/authStore'
import { useContextoStore } from '../store/contextoStore'
import { cn, formatDateTime } from '../lib/utils'
import { conectarNotificacoes } from '../lib/notificacoesRealtime'
import { NAV_SEARCH_INDEX } from './navigation'
import { useDismiss } from './useDismiss'

interface TopbarProps {
  onToggleSidebar: () => void
}

const topbarButton = 'relative flex h-[42px] w-[42px] items-center justify-center rounded-full text-[22px] text-muted transition-colors hover:bg-surface2 hover:text-white'

export function Topbar({ onToggleSidebar }: TopbarProps) {
  return (
    <header className="sticky top-0 z-30 flex h-[70px] flex-shrink-0 items-center justify-between gap-3 border-b border-border/70 bg-surface px-3 lg:px-4">
      <div className="flex items-center gap-2">
        <button type="button" onClick={onToggleSidebar} className={topbarButton} aria-label="Alternar menu">
          <i className="ri-menu-2-line" aria-hidden />
        </button>
        <MenuSearch />
      </div>

      <div className="flex items-center gap-1">
        <ContextoSwitcher />
        <FullscreenButton />
        <Notifications />
        <ProfileMenu />
      </div>
    </header>
  )
}

function MenuSearch() {
  const navigate = useNavigate()
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(boxRef, open, close)

  const results = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return []
    return NAV_SEARCH_INDEX.filter((r) => r.label.toLowerCase().includes(t) || r.trail.toLowerCase().includes(t)).slice(0, 6)
  }, [term])

  function go(to: string) {
    navigate(to)
    setTerm('')
    setOpen(false)
  }

  return (
    <div ref={boxRef} className="relative hidden md:block">
      <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted" aria-hidden />
      <input
        value={term}
        onChange={(e) => { setTerm(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' && results[0]) go(results[0].to) }}
        placeholder="Buscar no menu..."
        aria-label="Buscar no menu"
        className="h-[38px] w-64 rounded bg-surface2 pl-9 pr-3 text-13 text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 xl:w-80"
      />
      {open && term.trim() && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded bg-surface shadow-dropdown animate-fade-in">
          {results.length === 0 ? (
            <p className="px-4 py-3 text-13 text-muted">Nada encontrado para "{term}".</p>
          ) : (
            <>
              <p className="px-4 pb-1 pt-3 text-2xs font-semibold uppercase text-muted">Páginas</p>
              {results.map((r) => (
                <button key={r.to} type="button" onClick={() => go(r.to)} className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-surface2">
                  <i className={cn(r.icon, 'text-lg text-muted')} aria-hidden />
                  <span className="min-w-0">
                    <span className="block truncate text-13 text-white">{r.label}</span>
                    <span className="block truncate text-2xs text-muted">{r.trail}</span>
                  </span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function ContextoSwitcher() {
  const { selecionados, incluirPropria, principalId, alternarPropria, alternarConta, definirPrincipal, sincronizar } = useContextoStore()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)

  const { data: contextos = [] } = useQuery({
    queryKey: ['contextos'],
    queryFn: compartilhamentosApi.contextos,
    staleTime: 60_000,
  })

  useEffect(() => { if (contextos.length >= 0) sincronizar(contextos) }, [contextos, sincronizar])

  function comRecarga(acao: () => void) {
    acao()
    qc.invalidateQueries()
  }

  if (contextos.length === 0 && selecionados.length === 0) return null

  const total = (incluirPropria ? 1 : 0) + selecionados.length
  const rotulo = total > 1
    ? `${total} contas`
    : incluirPropria ? 'Minha conta' : selecionados[0]?.ownerNome ?? 'Minha conta'

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'flex h-[38px] items-center gap-2 rounded px-3 text-13 transition-colors',
          total > 1 || !incluirPropria ? 'bg-primary/15 text-primary' : 'bg-surface2 text-muted hover:text-white',
        )}
      >
        <i className={total > 1 ? 'ri-group-line' : incluirPropria ? 'ri-user-line' : 'ri-user-shared-line'} aria-hidden />
        <span className="hidden max-w-[160px] truncate sm:block">{rotulo}</span>
        <i className="ri-arrow-down-s-line" aria-hidden />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded bg-surface py-2 shadow-dropdown animate-fade-in">
          <p className="px-4 pb-1 text-2xs font-semibold uppercase text-muted">Somar dados de</p>

          <LinhaContexto
            nome="Minha conta"
            icone="ri-user-line"
            marcada={incluirPropria}
            ehPrincipal={principalId === null}
            onMarcar={() => comRecarga(alternarPropria)}
            onPrincipal={() => definirPrincipal(null)}
          />

          {contextos.map((c) => (
            <LinhaContexto
              key={c.ownerUserId}
              nome={c.ownerNome}
              detalhe={c.ownerEmail}
              icone="ri-user-shared-line"
              marcada={selecionados.some((s) => s.ownerUserId === c.ownerUserId)}
              ehPrincipal={principalId === c.ownerUserId}
              onMarcar={() => comRecarga(() => alternarConta(c))}
              onPrincipal={() => definirPrincipal(c.ownerUserId)}
            />
          ))}

          <p className="px-4 pt-2 text-2xs text-muted">
            A marcação define o que é somado nas telas. A estrela indica onde novos lançamentos são criados.
          </p>

          <div className="my-1 border-t border-border/60" />
          <Link to="/compartilhamento" onClick={close} className="flex items-center gap-2 px-4 py-2 text-13 text-white hover:bg-surface2">
            <i className="ri-share-line text-base text-muted" aria-hidden /> Gerenciar compartilhamento
          </Link>
        </div>
      )}
    </div>
  )
}

function LinhaContexto({
  nome, detalhe, icone, marcada, ehPrincipal, onMarcar, onPrincipal,
}: {
  nome: string
  detalhe?: string
  icone: string
  marcada: boolean
  ehPrincipal: boolean
  onMarcar: () => void
  onPrincipal: () => void
}) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 hover:bg-surface2">
      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
        <input type="checkbox" checked={marcada} onChange={onMarcar} className="h-4 w-4 accent-primary" />
        <i className={cn(icone, 'text-base text-muted')} aria-hidden />
        <span className="min-w-0">
          <span className="block truncate text-13 text-white">{nome}</span>
          {detalhe && <span className="block truncate text-2xs text-muted">{detalhe}</span>}
        </span>
      </label>
      <button
        type="button"
        onClick={onPrincipal}
        disabled={!marcada}
        title={ehPrincipal ? 'Recebe os novos lançamentos' : 'Usar esta conta para novos lançamentos'}
        aria-label={ehPrincipal ? `${nome} recebe os novos lançamentos` : `Usar ${nome} para novos lançamentos`}
        className={cn('flex h-7 w-7 items-center justify-center rounded transition-colors disabled:opacity-30',
          ehPrincipal ? 'text-primary' : 'text-muted hover:text-white')}
      >
        <i className={ehPrincipal ? 'ri-star-fill' : 'ri-star-line'} aria-hidden />
      </button>
    </div>
  )
}

function FullscreenButton() {
  const [isFull, setIsFull] = useState(false)

  useEffect(() => {
    const onChange = () => setIsFull(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  function toggle() {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {})
    else document.documentElement.requestFullscreen().catch(() => {})
  }

  return (
    <button type="button" onClick={toggle} className={cn(topbarButton, 'hidden sm:flex')} aria-label={isFull ? 'Sair da tela cheia' : 'Tela cheia'}>
      <i className={isFull ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} aria-hidden />
    </button>
  )
}

/** Ícone e cor por tipo de notificação (o servidor manda o nome do tipo). */
function estiloNotificacao(tipo: string): { icone: string; cor: string } {
  switch (tipo) {
    case 'ManutencaoPerto': return { icone: 'ri-tools-line', cor: 'bg-warning/10 text-warning' }
    case 'ManutencaoVencida': return { icone: 'ri-tools-line', cor: 'bg-expense/10 text-expense' }
    case 'VencimentoVeiculo': return { icone: 'ri-calendar-event-line', cor: 'bg-warning/10 text-warning' }
    case 'AtualizarKm': return { icone: 'ri-speed-up-line', cor: 'bg-info/10 text-info' }
    case 'ParcelaFinanciamentoAtrasada': return { icone: 'ri-alarm-warning-line', cor: 'bg-expense/10 text-expense' }
    case 'ParcelaFinanciamentoVencendo': return { icone: 'ri-calendar-event-line', cor: 'bg-warning/10 text-warning' }
    case 'FaturaVencendo': return { icone: 'ri-bank-card-line', cor: 'bg-warning/10 text-warning' }
    case 'OrcamentoAlerta': return { icone: 'ri-pie-chart-2-line', cor: 'bg-warning/10 text-warning' }
    case 'CorrecaoMonetariaAplicada': return { icone: 'ri-line-chart-line', cor: 'bg-info/10 text-info' }
    default: return { icone: 'ri-refresh-line', cor: 'bg-info/10 text-info' }
  }
}

function Notifications() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)
  const qc = useQueryClient()

  const { data: notificacoes = [] } = useQuery({
    queryKey: ['notificacoes'],
    queryFn: notificacoesApi.list,
    staleTime: 30_000,
  })
  const naoLidas = notificacoes.filter((n) => !n.lida)

  // Tempo real enquanto a página está aberta; sem isso, só atualiza quando o staleTime vence.
  useEffect(() => conectarNotificacoes(() => qc.invalidateQueries({ queryKey: ['notificacoes'] })), [qc])

  const marcarLida = (id: string) => {
    notificacoesApi.marcarLida(id).catch(() => undefined)
    qc.setQueryData(['notificacoes'], (atual: typeof notificacoes = []) =>
      atual.map((n) => (n.id === id ? { ...n, lida: true } : n)))
  }

  const marcarTodasLidas = () => {
    notificacoesApi.marcarTodasLidas().catch(() => undefined)
    qc.setQueryData(['notificacoes'], (atual: typeof notificacoes = []) => atual.map((n) => ({ ...n, lida: true })))
  }

  const totalBadge = naoLidas.length

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className={topbarButton} aria-label={`Notificações (${totalBadge})`} aria-expanded={open}>
        <i className="ri-notification-3-line" aria-hidden />
        {totalBadge > 0 && (
          <span className="absolute right-1 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-expense px-1 text-2xs font-semibold text-white">
            {totalBadge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded bg-surface shadow-dropdown animate-fade-in">
          <div className="flex items-center justify-between bg-gradient-primary px-4 py-3">
            <h6 className="text-base font-semibold text-white">Notificações</h6>
            {naoLidas.length > 0 && (
              <button type="button" onClick={marcarTodasLidas} className="text-2xs font-semibold text-white/90 hover:text-white">
                Marcar todas como lidas
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notificacoes.length === 0 ? (
              <div className="flex flex-col items-center px-4 py-8 text-center">
                <i className="ri-notification-off-line text-3xl text-muted" aria-hidden />
                <p className="mt-2 text-13 text-muted">Nada por aqui ainda.</p>
              </div>
            ) : (
              <>
                {notificacoes.map((n) => (
                  <Link
                    key={n.id} to={n.link ?? '#'} onClick={() => { marcarLida(n.id); close() }}
                    className="flex items-start gap-3 border-b border-border/60 px-4 py-3 last:border-b-0 hover:bg-surface2"
                  >
                    <span className={cn('flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full', estiloNotificacao(n.tipo).cor)}>
                      <i className={estiloNotificacao(n.tipo).icone} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-13 text-white">{n.titulo}</span>
                      {n.corpo && <span className="block text-xs text-muted">{n.corpo}</span>}
                      <span className="block text-2xs text-muted">{formatDateTime(n.criadaEm)}</span>
                    </span>
                    {!n.lida && <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />}
                  </Link>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ProfileMenu() {
  const { userName, userEmail, userRole, logout } = useAuthStore()
  const [open, setOpen] = useState(false)

  /** Sai só deste navegador: o servidor encerra a sessão dele e os outros aparelhos continuam. */
  function sair() {
    const refreshToken = useAuthStore.getState().refreshToken
    logout()
    // Sem rede a sessão fica no servidor até vencer; o token já foi apagado daqui.
    if (refreshToken) authApi.sair(refreshToken).catch(() => undefined)
  }

  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)

  const initials = (userName ?? 'U').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()

  return (
    <div ref={ref} className="relative ml-1 h-[70px]">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex h-full items-center gap-2.5 bg-surface2/60 px-3 hover:bg-surface2" aria-expanded={open} aria-label="Menu do usuário">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-primary text-xs font-bold text-white">{initials}</span>
        <span className="hidden text-left xl:block">
          <span className="block text-13 font-medium text-white">{userName ?? 'Usuário'}</span>
          <span className="block text-2xs text-muted">{userRole ?? '—'}</span>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 w-52 overflow-hidden rounded bg-surface py-2 shadow-dropdown animate-fade-in">
          <div className="px-4 pb-2">
            <p className="text-13 text-white">Olá, {userName?.split(' ')[0] ?? 'usuário'}!</p>
            {userEmail && <p className="truncate text-2xs text-muted">{userEmail}</p>}
          </div>
          <Link to="/configuracoes" onClick={close} className="flex items-center gap-2 px-4 py-2 text-13 text-white hover:bg-surface2">
            <i className="ri-settings-3-line text-base text-muted" aria-hidden /> Configurações
          </Link>
          <Link to="/usuarios" onClick={close} className="flex items-center gap-2 px-4 py-2 text-13 text-white hover:bg-surface2">
            <i className="ri-team-line text-base text-muted" aria-hidden /> Usuários
          </Link>
          <div className="my-1 border-t border-border/60" />
          <button type="button" onClick={sair} className="flex w-full items-center gap-2 px-4 py-2 text-13 text-white hover:bg-surface2">
            <i className="ri-logout-box-r-line text-base text-muted" aria-hidden /> Sair
          </button>
        </div>
      )}
    </div>
  )
}
